import {
  ArticleType,
  MetricsGroupBy,
  ReportSection,
} from 'app/modules/reportDashboards/models';
import { YAxisType } from 'app/modules/reportDashboards/enums';
import permissions from 'app/shared/utils/permissions';

export const MAX_ITEMS_IN_CHART_TOOLTIP = 10;

// Defines chart config for each section + initial values (enabled, chartType)
export const DEFAULT_REPORT_CHARTS: ReportSection[] = [
  {
    id: 'kpis',
    title: 'KPIs',
    permissions: [permissions.readCases],
    stats: [],
    charts: [
      {
        id: 'kpiAlertsToCaseToSars',
        type: 'alert-kpi',
        title: 'Alert to Case to SAR',
        enabled: true,
        groupBy: 'alert',
        permissions: [permissions.readSars, permissions.readAlerts],
      },
      {
        id: 'kpiAlertsToCase',
        type: 'alert-case-kpi',
        title: 'Alert to Case',
        enabled: true,
        groupBy: 'alert',
        permissions: [permissions.readAlerts],
      },
      {
        id: 'kpiCaseToSars',
        type: 'case-kpi',
        title: 'Case to SAR',
        enabled: true,
        groupBy: 'case',
        permissions: [permissions.readSars],
      },
    ],
  },
  {
    id: 'alerts',
    title: 'Alerts',
    permissions: [permissions.readAlerts],
    stats: [
      {
        type: 'total-alerts',
      },
      {
        type: 'total-alerts-closed',
      },
      {
        type: 'total-alerts-dispositioned',
      },
    ],
    charts: [
      {
        id: 'alertsGeneratedOverTime',
        type: 'alert-creation',
        title: 'Total Alerts Generated Over Time',
        tooltip:
          'Shows alerts generated within the selected time period. Use to analyze trends or activity peaks.',
        enabled: true,
        chartType: 'line',
      },
      {
        id: 'alertCreationPerRule',
        type: 'alert-creation',
        title: 'Alerts Generated per Rule',
        permissions: [permissions.readRules],
        tooltip:
          'Shows alerts generated by rules. Note that alerts can be triggered by multiple rules. Alerts must have been created within the selected time period.',
        enabled: false,
        chartType: 'bar',
        groupBy: 'rule',
      },
      {
        id: 'alertCreationPerQueue',
        type: 'alert-creation',
        tooltip:
          'Shows which queues are receiving the most new alerts. Alerts must have been created within the selected time period.',
        title: 'Alerts Generated per Queue',
        enabled: true,
        chartType: 'bar',
        groupBy: 'queue',
      },
      {
        id: 'alertCreationPerAgingBucket',
        type: 'alert-creation',
        title: 'Alerts Aging',
        tooltip:
          'Shows created alerts by age bucket within the selected time period. Analyze trends or peak alert activity.',
        enabled: false,
        chartType: 'bar',
        groupBy: 'aging_bucket',
      },
      {
        id: 'alertOpenPerAgent',
        type: 'alert-creation',
        title: 'Alerts Open per Agent',
        tooltip:
          'Shows alerts created within the selected time period and in open status by agent.',
        enabled: false,
        chartType: 'bar',
        groupBy: 'agent',
      },
      {
        id: 'alertDispositionPerDisposition',
        type: 'alert-action',
        title: 'Alerts Dispositioned per Disposition',
        tooltip:
          'Shows alerts created within the selected time period and grouped by disposition type.',
        enabled: true,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'disposition',
      },
      {
        id: 'alertDispositionPerAgent',
        type: 'alert-action',
        title: 'Alerts Dispositioned per Agent',
        tooltip:
          'Shows alerts created within the selected time period and dispositioned by agent. Note that alerts can have multiple dispositions by different agents.',
        enabled: false,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'agent',
      },
      {
        id: 'alertClosedPerQueue',
        type: 'alert-action',
        title: 'Alert Closed per Queue',
        tooltip:
          'Shows alerts created and closed by queue within the selected time period. Effective queue management can reduce alert volume and improve agent throughput.',
        enabled: false,
        chartType: 'bar',
        action: 'closed',
        groupBy: 'queue',
      },
      {
        id: 'alertClosedPerAgent',
        type: 'alert-action',
        title: 'Alert Closed per Agent',
        tooltip:
          'Shows the total number of alerts created and closed by each agent within the selected time period. Use to analyze agent throughput or identify bottlenecks.',
        enabled: true,
        chartType: 'bar',
        action: 'closed',
        groupBy: 'agent',
      },
      {
        id: 'alertAvgResolutionTimePerAgent',
        type: 'alert-action-duration',
        title: 'Average Time Spent on Alert per Agent',
        tooltip:
          'Shows the average time spent from alert creation to completion by each agent within the selected time period.',
        enabled: true,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'agent',
        durationMetric: 'avg_resolution_time',
      },
    ],
  },
  {
    id: 'cases',
    title: 'Cases',
    permissions: [permissions.readCases],
    stats: [
      {
        type: 'total-cases',
      },
      {
        type: 'total-cases-dispositioned',
      },
    ],
    charts: [
      {
        id: 'caseCreationPerAgent',
        type: 'case-creation',
        title: 'Cases Created per Agent',
        tooltip:
          'Shows cases created by each agent within the selected time period. Use to analyze agent throughput or agent-confirmed alert to case creation.',
        enabled: true,
        chartType: 'bar',
        groupBy: 'agent',
      },
      {
        id: 'caseCreationPerQueue',
        type: 'case-creation',
        title: 'Cases Created per Queue',
        tooltip:
          'Shows created cases grouped by current queue within the selected time period. Cases moved between queues will appear in their current queue.',
        enabled: true,
        chartType: 'bar',
        groupBy: 'queue',
      },
      {
        id: 'caseCreationPerAgingBucket',
        type: 'case-creation',
        title: 'Cases Aging',
        tooltip:
          'Shows created cases by age bucket within the selected time period. Analyze trends or peak case activity.',
        enabled: false,
        chartType: 'bar',
        groupBy: 'aging_bucket',
      },
      {
        id: 'caseDispositionPerDisposition',
        type: 'case-action',
        title: 'Cases Dispositioned per Disposition',
        tooltip:
          'Shows cases created within the selected time period and grouped by disposition type.',
        enabled: true,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'disposition',
      },
      {
        id: 'caseDispositionPerAgent',
        type: 'case-action',
        title: 'Cases Dispositioned per Agent',
        tooltip:
          'Shows cases created within the selected time period and dispositioned by agent. Note that cases can have multiple dispositions by different agents.',
        enabled: false,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'agent',
      },
      {
        id: 'caseClosedPerQueue',
        type: 'case-action',
        title: 'Cases Closed per Queue',
        tooltip:
          'Shows casess created and closed by queue within the selected time period. Effective queue management can reduce alert volume and improve agent throughput.',
        enabled: false,
        chartType: 'bar',
        action: 'closed',
        groupBy: 'queue',
      },
      {
        id: 'caseClosedPerAgent',
        type: 'case-action',
        title: 'Cases Closed per Agent',
        tooltip:
          'Shows the total number of cases created and closed by each agent within the selected time period. Use to analyze agent throughput or identify bottlenecks.',
        enabled: true,
        chartType: 'bar',
        action: 'closed',
        groupBy: 'agent',
      },
      {
        id: 'caseAvgResolutionTimePerAgent',
        type: 'case-action-duration',
        title: 'Average Time Spent on Case per Agent',
        tooltip:
          'Shows the average time spent from case creation to completion by each agent within the selected time period. Note that cases included in this metric must have been created within the selected time period.',
        enabled: true,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'agent',
        durationMetric: 'avg_resolution_time',
      },
    ],
  },
  {
    id: 'sars',
    title: 'SARs',
    permissions: [permissions.readSars],
    stats: [
      {
        type: 'total-sars',
      },
      {
        type: 'total-sars-completed-on-time',
      },
      {
        type: 'total-sars-failed-or-rejected',
      },
    ],
    charts: [
      {
        id: 'sarCreationOverTime',
        type: 'sar-creation',
        title: 'SARs Created Over Time',
        tooltip:
          'Shows all SARs created within the selected time period. Use to analyze trends or activity peaks.',
        enabled: true,
        chartType: 'bar',
      },
      {
        id: 'sarCreationPerAgent',
        type: 'sar-creation',
        title: 'SARs Created per Agent',
        tooltip:
          'Shows SARs filed by each agent within the selected time period. Note that this metric is for the submitter, not necessarily the responder.',
        enabled: true,
        chartType: 'bar',
        groupBy: 'agent',
      },
      {
        id: 'sarCompletedPerAgent',
        type: 'sar-action',
        title: 'SARs Completed per Agent',
        tooltip:
          'Shows SARs created and completed by each agent within the selected time period. Use to analyze agent throughput or identify bottlenecks.',
        enabled: true,
        chartType: 'bar',
        action: 'submitted',
        groupBy: 'agent',
      },
      {
        id: 'sarAvgResolutionTimePerAgent',
        type: 'sar-action-duration',
        title: 'Average Time Spent on SAR per Agent',
        tooltip:
          'Shows the average time spent from SAR creation to completion by each agent within the selected time period.',
        enabled: true,
        action: 'submitted',
        groupBy: 'agent',
        durationMetric: 'avg_resolution_time',
      },
    ],
  },
];

export const ARTICLE_LABEL: Record<ArticleType, string> = {
  alert: 'Alert',
  case: 'Case',
  sar: 'SAR',
};

export const X_AXIS_LABEL: Record<MetricsGroupBy, string> = {
  agent: 'Agents',
  queue: 'Queues',
  rule: 'Rules',
  disposition: 'Dispositions',
  type: 'Types',
  aging_bucket: 'Age',
};

export const Y_AXIS_LABEL: Record<YAxisType, string> = {
  [YAxisType.IDS]: 'Count',
  [YAxisType.TIME]: 'Time',
};
